import React, { useState } from 'react';
import { X, Play, CheckCircle2, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';
import { useWorkflowStore } from '../../hooks/useWorkflowStore';

export default function FlowTestSimulator({ onClose }) {
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);

  const [testLead, setTestLead] = useState({
    name: 'Sarah Connor',
    company: 'Skynet Cybernetics',
    email: 'sarah@skynet.io',
    lead_score: 75,
    industry: 'Technology',
  });

  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [completed, setCompleted] = useState(false);

  const runSimulation = async () => {
    setRunning(true);
    setLogs([]);
    setCompleted(false);

    const triggerNode = nodes.find((n) => n.type === 'start');
    if (!triggerNode) {
      setLogs([
        {
          type: 'error',
          text: '❌ Simulation Error: No Start node found in the workflow.',
        },
      ]);
      setRunning(false);
      return;
    }

    setLogs((prev) => [
      ...prev,
      {
        type: 'info',
        text: `⚡ Ingested Lead: ${testLead.name} (${testLead.company}) - Score: ${testLead.lead_score}`,
      },
    ]);

    await new Promise((r) => setTimeout(r, 600));

    // Find conditions or connected nodes
    let currentId = triggerNode.id;
    let stepCount = 0;

    while (currentId && stepCount < 10) {
      stepCount++;
      const currentNode = nodes.find((n) => n.id === currentId);
      if (!currentNode) break;

      setLogs((prev) => [
        ...prev,
        {
          type: 'step',
          text: `▶ Executing Block: [${currentNode.type.toUpperCase()}] ${currentNode.data.title}`,
        },
      ]);

      await new Promise((r) => setTimeout(r, 600));

      if (currentNode.type === 'condition') {
        const fieldVal = Number(testLead.lead_score);
        const targetVal = Number(currentNode.data.value || 50);
        const conditionPassed = fieldVal > targetVal;

        const branch = conditionPassed ? 'true' : 'false';
        setLogs((prev) => [
          ...prev,
          {
            type: conditionPassed ? 'success' : 'warning',
            text: `🔀 Condition Check (${fieldVal} > ${targetVal}): Evaluated to ${branch.toUpperCase()}`,
          },
        ]);

        const nextEdge = edges.find(
          (e) => e.source === currentId && e.sourceHandle === branch
        );
        currentId = nextEdge ? nextEdge.target : null;
      } else if (currentNode.type === 'probability') {
        const branches = currentNode.data.branches || [];
        const totalWeight = branches.reduce((sum, b) => sum + (Number(b.weight) || 0), 0) || 100;
        const randomRoll = Math.random() * totalWeight;

        let accumulated = 0;
        let chosenBranch = branches[0];
        for (const b of branches) {
          accumulated += Number(b.weight) || 0;
          if (randomRoll <= accumulated) {
            chosenBranch = b;
            break;
          }
        }

        const chosenHandle = chosenBranch?.branch_id;
        const chosenLabel = chosenBranch?.label || chosenHandle;

        setLogs((prev) => [
          ...prev,
          {
            type: 'success',
            text: `🎲 Probability Roll (${randomRoll.toFixed(1)} / 100%): Selected ${chosenLabel}`,
          },
        ]);

        const nextEdge = edges.find(
          (e) => e.source === currentId && e.sourceHandle === chosenHandle
        );
        currentId = nextEdge ? nextEdge.target : null;
      } else if (currentNode.type === 'offerwall') {
        const rawUrl = currentNode.data?.redirect_url || 'https://offers.com/wall?lead={lead_id}';
        const formattedUrl = rawUrl.replace('{lead_id}', '1001').replace('{sub_id}', 'test_sub');
        const payout = currentNode.data?.payout ? `$${currentNode.data.payout}` : 'No payout';

        setLogs((prev) => [
          ...prev,
          {
            type: 'success',
            text: `🎯 Offerwall Fallback Redirected: ${formattedUrl} (${payout})`,
          },
        ]);
        currentId = null;
      } else {
        const nextEdge = edges.find((e) => e.source === currentId);
        currentId = nextEdge ? nextEdge.target : null;
      }

    }

    setLogs((prev) => [
      ...prev,
      {
        type: 'finish',
        text: '✅ Flow Execution Completed Successfully!',
      },
    ]);

    setRunning(false);
    setCompleted(true);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 600,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'var(--amber-bg)',
                color: 'var(--amber)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Play size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                Lead Routing Simulator
              </h3>
              <p
                style={{
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  margin: 0,
                }}
              >
                Test your workflow with simulated lead payloads
              </p>
            </div>
          </div>

          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>
              Simulated Lead Input
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  Lead Name
                </span>
                <input
                  type="text"
                  className="form-control"
                  value={testLead.name}
                  onChange={(e) =>
                    setTestLead({ ...testLead, name: e.target.value })
                  }
                />
              </div>
              <div>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  Lead Score
                </span>
                <input
                  type="number"
                  className="form-control"
                  value={testLead.lead_score}
                  onChange={(e) =>
                    setTestLead({ ...testLead, lead_score: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%', gap: 8 }}
            onClick={runSimulation}
            disabled={running}
          >
            {running ? (
              <>
                <RefreshCw size={16} className="spin" /> Simulating Flow...
              </>
            ) : (
              <>
                <Play size={16} /> Run Test Simulation
              </>
            )}
          </button>

          {/* Console Log */}
          {logs.length > 0 && (
            <div
              style={{
                marginTop: 20,
                padding: 14,
                borderRadius: 10,
                background: 'var(--bg-base)',
                border: '1px solid var(--border)',
                fontFamily: 'monospace',
                fontSize: 12,
                maxHeight: 220,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {logs.map((log, i) => (
                <div
                  key={i}
                  style={{
                    color:
                      log.type === 'error'
                        ? 'var(--red)'
                        : log.type === 'finish'
                        ? 'var(--green)'
                        : log.type === 'success'
                        ? 'var(--green)'
                        : log.type === 'warning'
                        ? 'var(--amber)'
                        : 'var(--text-primary)',
                  }}
                >
                  {log.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
